import { useAuthStore } from '@services/pinia/AuthStore';
import { capitalize } from 'lodash';

export const formatDateTime = (dateStr) => {
    if (dateStr) {
        const date = new Date(dateStr.replace(' ', 'T'));
        return date.toLocaleString('sv-SE');
    }
    return '';
};

export const getDayName = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('sv-SE', { weekday: 'short' });
};

export const sortLabelByAZ = (a, b) => {
    if (a.label > b.label) {
        return 1;
    }
    if (b.label > a.label) {
        return -1;
    }
    return 0;
};

export const getWeek = (dateStr) => {
    // Create a copy of this date object
    const target = new Date(dateStr);

    // ISO week date weeks start on monday
    // so correct the day number
    const dayNr = (target.getDay() + 6) % 7;

    // ISO 8601 states that week 1 is the week
    // with the first thursday of that year.
    // Set the target date to the thursday in the target week
    target.setDate(target.getDate() - dayNr + 3);

    // Store the millisecond value of the target date
    const firstThursday = target.valueOf();

    // Set the target to the first thursday of the year
    // First set the target to january first
    target.setMonth(0, 1);
    // Not a thursday? Correct the date to the next thursday
    if (target.getDay() !== 4) {
        target.setMonth(0, 1 + (((4 - target.getDay()) + 7) % 7));
    }

    // The weeknumber is the number of weeks between the
    // first thursday of the year and the thursday in the target week
    return 1 + Math.ceil((firstThursday - target) / 604800000); // 604800000 = 7 * 24 * 3600 * 1000
};

/* eslint-disable */
export const genUUID = () => {
    return ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
        (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
    );
};
/* eslint-enable */

export const genMod10 = (code) => {
    // eslint-disable-next-line no-param-reassign
    code = String(code + '0');
    const len = code.length;
    const parity = len % 2;
    let sum = 0;
    // eslint-disable-next-line no-plusplus
    for (let i = len - 1; i >= 0; i--) {
        let d = parseInt(code.charAt(i), 10);
        if (i % 2 === parity) {
            d *= 2;
        }
        if (d > 9) {
            d -= 9;
        }
        sum += d;
    }
    sum %= 10;
    return (sum === 0 ? 0 : 10 - sum);
};

export const checkMod10 = (code) => {
    // eslint-disable-next-line no-param-reassign
    code = String(code);
    const len = code.length;
    const parity = len % 2;
    let sum = 0;
    // eslint-disable-next-line no-plusplus
    for (let i = len - 1; i >= 0; i--) {
        let d = parseInt(code.charAt(i), 10);
        if (i % 2 === parity) {
            d *= 2;
        }
        if (d > 9) {
            d -= 9;
        }
        sum += d;
    }
    return (sum % 10) === 0;
};

export const openPopupWindow = (url, target = 'default_popup_target', width = 1000, height = 800) => {
    if (url) {
        const popup = window.open(url, target, 'toolbar=0,location=0,directories=0,status=0,menubar=0,scrollbars=0,resizable=1,width=' + width + ',height=' + height + '');
        popup.focus();
    }
};

export const hasPermission = (permissionName) => {
    const authUser = useAuthStore().user;
    return !!(authUser && authUser.permissions && authUser.permissions.includes(permissionName));
};

export const hasRole = (roleName) => {
    const authUser = useAuthStore().user;
    return !!(authUser && authUser.roles && authUser.roles.includes(roleName));
};

export const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) {
        return '0 Bytes';
    }

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / (k ** i)).toFixed(dm)) + ' ' + sizes[i];
};

export const stringToColour = (str) => {
    /* eslint no-bitwise: off */
    /* eslint no-plusplus: off */
    let i;
    let hash = 0;

    for (i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    let colour = '#';
    for (i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xFF;
        colour += ('00' + value.toString(16)).substr(-2);
    }

    return colour;
};

export const formatCurrency = (amount, decimals = 2, currency = 'SEK', locale = 'sv-SE') => new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
}).format(amount);

export const formatNumber = (amount, decimals = 2, locale = 'sv-SE') => new Intl.NumberFormat(locale, {
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
}).format(amount);

export const nl2br = (str) => str.replace(/(?:\r\n|\r|\n)/g, '<br>');

export const nestedAssign = (target, source) => {
    if (typeof source === 'object' && !Array.isArray(source) && typeof target === 'object' && !Array.isArray(target)) {
        Object.keys(source).forEach((sourceKey) => {
            if (source[sourceKey] !== null && source[sourceKey] !== undefined && typeof source[sourceKey] === 'object' && !Array.isArray(source[sourceKey])) {
                target[sourceKey] = nestedAssign(target[sourceKey] || {}, source[sourceKey]);
            } else {
                target[sourceKey] = (typeof source[sourceKey] === 'string' && source[sourceKey].match(/^[0-9]+$/) ? parseFloat(source[sourceKey]) : source[sourceKey]);
            }
        });
    }

    return target;
};

export const getUrlParameters = () => {
    const search = window.location.search;
    const query = {};
    // avoid creating an object with an empty key and empty value
    // because of split('&')
    if (search === '' || search === '?') return query;
    const hasLeadingIM = search[0] === '?';
    const searchParams = (hasLeadingIM ? search.slice(1) : search).split('&');
    const pattern = /^\d+$/;
    const regex = new RegExp(pattern);
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < searchParams.length; ++i) {
        // pre decode the + into space
        const searchParam = searchParams[i].replace(/\+/g, ' ');
        // allow the = character
        const eqPos = searchParam.indexOf('=');
        const key = decodeURIComponent('' + (eqPos < 0 ? searchParam : searchParam.slice(0, eqPos)));
        let value = eqPos < 0 ? null : decodeURIComponent('' + searchParam.slice(eqPos + 1));
        if (value === 'null') {
            value = null;
        } else if (value === 'true') {
            value = true;
        } else if (value === 'false') {
            value = false;
        } else if (regex.test(value)) {
            value = parseInt(value, 10);
        }

        const realKey = key.replace(/([^a-zA-Z0-9-_])/g, '');
        if (realKey in query || key.endsWith('[]')) {
            if (!Array.isArray(query[realKey])) {
                query[realKey] = realKey in query ? [query[realKey]] : [];
            }
            query[realKey].push(value);
        } else {
            query[realKey] = value;
        }
    }
    return query;
};

export const round = (number, decimals = 0) => {
    // eslint-disable-next-line no-restricted-properties
    const factor = Math.pow(10, decimals);
    return Math.sign(number) * (Math.round(Math.abs(number * factor)) / factor);
};

export const splitAndCapitalize = (string, split = '_') => (string ? string.split(split).map(capitalize).join(' ') : null);
